/**
 * Pure-JS M3U / Xtream playlist parser.
 *
 * Port of /app/backend/m3u_parser.py — same field names, same detection rules.
 * Runs entirely client-side. The backend no longer parses or stores playlist
 * content; the admin panel only stores `{name, m3u_url, epg_url}`.
 */

const SERIES_PATTERNS = [
  // Season can be 1-4 digits to cover year-as-season formats common in
  // documentary / sports / annual-special shows (e.g. "Shark Week S2024 E1",
  // "Wimbledon S2023 E07"). Episode stays 1-3 digits.
  /[\s.]?S(\d{1,4})[\s.]?E(\d{1,3})/i,
  /[\s.]?Season[\s.]?(\d{1,4})[\s.]?Episode[\s.]?(\d{1,3})/i,
  /[\s.](\d{1,4})x(\d{1,3})[\s.]/i,
];

function parseAttributes(line) {
  // Parse tvg-id="..." group-title="..." catchup-source="..." etc.
  const attrs = {};
  const re = /([\w-]+)="([^"]*)"/g;
  let m;
  while ((m = re.exec(line)) !== null) {
    attrs[m[1]] = m[2];
  }
  return attrs;
}

export function detectContentType(group, name, streamUrl) {
  const g = (group || "").toLowerCase();
  const n = (name || "").toLowerCase();
  // Strip query string before extension/path matching so URLs that
  // carry auth tokens (e.g. Plex: `.../file.mkv?X-Plex-Token=...`,
  // or signed CDN links) are detected as movies/series instead of
  // silently falling through to "live_tv" — which is what was
  // breaking poster + TMDB enrichment for Plex-backed playlists.
  const uFull = (streamUrl || "").toLowerCase();
  const u = uFull.split("?")[0].split("#")[0];

  // URL hints — Xtream uses /series/ /movie/ /live/
  if (u.includes("/series/") || u.includes("/serie/")) return "series";
  if (u.includes("/movie/") || u.includes("/vod/")) return "movie";
  if (u.includes("/live/")) return "live_tv";

  if (g.includes("series") || g.includes("show") || g.includes("tv shows")) return "series";
  if (g.includes("movie") || g.includes("film") || g.includes("vod") || g.includes("cinema"))
    return "movie";
  if (g.includes("catchup") || g.includes("catch up") || g.includes("catch-up")) return "catchup";

  // Filename hints — match against the path-only URL so auth tokens
  // / query params don't mask the underlying extension.
  if (u.endsWith(".mp4") || u.endsWith(".mkv") || u.endsWith(".avi")) {
    for (const re of SERIES_PATTERNS) if (re.test(n)) return "series";
    return "movie";
  }

  return "live_tv";
}

export function extractSeriesMeta(name) {
  for (const re of SERIES_PATTERNS) {
    const m = name.match(re);
    if (m) {
      // Cleanup show name
      const showRaw = name.slice(0, m.index).replace(/[._]+$/, "").trim();
      return {
        series_name: showRaw || name,
        season: parseInt(m[1], 10) || null,
        episode: parseInt(m[2], 10) || null,
      };
    }
  }
  return { series_name: name, season: null, episode: null };
}

export function extractYear(name) {
  const m = name.match(/\((19|20)\d{2}\)/) || name.match(/[\s.](19|20)\d{2}[\s.\b]/);
  if (!m) return null;
  const y = parseInt(m[0].replace(/[^0-9]/g, ""), 10);
  return y >= 1900 && y <= 2100 ? y : null;
}

/**
 * Parse a raw M3U / M3U8 playlist string into an array of channel objects.
 * Each object has a stable `id` derived from the stream URL.
 *
 * Also returns the `epg_url` (`url-tvg` / `x-tvg-url`) when declared on the
 * `#EXTM3U` header line — handy when the playlist embeds its own EPG.
 */
export async function parseM3u(text, onProgress) {
  if (!text || typeof text !== "string") {
    return { channels: [], epgUrl: null };
  }

  const channels = [];
  let epgUrl = null;
  let order = 0;

  // Streaming walk — we slice through `text` with indexOf("\n") instead of
  // calling `text.split("\n")` which would allocate a 1M-element string
  // array up front (~200 MB peak on a 590k-channel IPTV playlist). This
  // way memory stays roughly constant: one line in flight + the result
  // array. Critical on Smart TVs with ~256 MB WebView heap budget.
  const len = text.length;
  let i = 0;
  let yieldCounter = 0;
  // Yield every ~1500 channels (~3000 lines) — more frequent than the
  // previous 5000 so the TV's renderer gets a chance to paint the
  // progress bar early and the user never sees a frozen "Items so far: 0".
  const YIELD_EVERY = 1500;
  // Emit progress every 500 channels so the splash counter ticks visibly
  // even on small playlists.
  const PROGRESS_EVERY = 500;

  // Tiny inline helper — reads one line from `text` starting at `i`,
  // advances `i` past the trailing newline (and the CR if it's a \r\n
  // line ending). Returns the line as a fresh string so V8 doesn't keep
  // the entire source `text` alive via slice's substring-shared
  // representation.
  function readLine() {
    if (i >= len) return null;
    let nl = text.indexOf("\n", i);
    if (nl === -1) nl = len;
    let end = nl;
    if (end > i && text.charCodeAt(end - 1) === 13 /* \r */) end -= 1;
    // The `("" + slice)` form forces V8 to materialise an independent
    // string rather than holding a pointer into the original `text`.
    const out = "" + text.slice(i, end);
    i = nl + 1;
    return out;
  }

  // First line is usually #EXTM3U — may carry an EPG URL.
  const first = readLine();
  if (first && first.trim().startsWith("#EXTM3U")) {
    const headerAttrs = parseAttributes(first);
    epgUrl = headerAttrs["url-tvg"] || headerAttrs["x-tvg-url"] || null;
  } else if (first) {
    // No header — push back by re-parsing this line in the loop below.
    // Cheapest way: temporarily rewind `i`. But `first` had its newline
    // already consumed, so we instead fall through with `first` as the
    // current line.
    // (Most IPTV providers ship #EXTM3U on line 1 so this branch is rare.)
    i = 0;
  }

  while (i < len) {
    const line = readLine();
    if (line == null) break;
    if (!line.startsWith("#EXTINF:")) continue;

    const after = line.slice(8);
    // Find the comma separating attrs from title (skip commas inside quotes)
    let inQuotes = false;
    let splitIdx = -1;
    for (let j = 0; j < after.length; j++) {
      const ch = after[j];
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === "," && !inQuotes) {
        splitIdx = j;
        break;
      }
    }
    const attrPart = splitIdx === -1 ? "" : after.slice(0, splitIdx);
    const name = (splitIdx === -1 ? after : after.slice(splitIdx + 1)).trim();
    const attrs = parseAttributes(attrPart);

    // Next non-comment, non-blank line is the stream URL.
    let url = null;
    while (i < len) {
      const t = readLine();
      if (t == null) break;
      const trimmed = t.trim();
      if (!trimmed) continue;
      if (trimmed.startsWith("#")) continue;
      url = trimmed;
      break;
    }
    if (!url || !name) continue;

    const group = attrs["group-title"] || "";
    const contentType = detectContentType(group, name, url);

    // Stable id from stream URL — FNV-1a 32-bit hash (fast, collision-safe enough).
    let h = 0x811c9dc5;
    for (let k = 0; k < url.length; k++) {
      h ^= url.charCodeAt(k);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    const id = `c-${order}-${h.toString(36)}`;

    const channel = {
      id,
      name,
      stream_url: url,
      logo_url: attrs["tvg-logo"] || null,
      group: group || null,
      content_type: contentType,
      tvg_id: attrs["tvg-id"] || null,
      tvg_name: attrs["tvg-name"] || null,
      catchup: attrs["catchup"] || attrs["catchup-type"] || null,
      catchup_source: attrs["catchup-source"] || null,
      catchup_days:
        parseInt(attrs["catchup-days"] || attrs["timeshift"] || "", 10) || null,
      order: order++,
    };
    if (contentType === "series") Object.assign(channel, extractSeriesMeta(name));
    if (contentType === "movie") channel.year = extractYear(name);

    channels.push(channel);
    yieldCounter++;

    if (channels.length % PROGRESS_EVERY === 0 && typeof onProgress === "function") {
      try {
        onProgress({ phase: "parse", done: channels.length });
      } catch {
        /* listener errors must not break the loop */
      }
    }
    if (yieldCounter >= YIELD_EVERY) {
      yieldCounter = 0;
      // setTimeout(0) lets the GC + paint loop breathe between chunks.
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  // Final progress tick so the splash reflects the true total even when
  // the last batch was smaller than PROGRESS_EVERY.
  if (typeof onProgress === "function") {
    try {
      onProgress({ phase: "parse", done: channels.length });
    } catch {
      /* ignore */
    }
  }

  return { channels, epgUrl };
}

/**
 * Build groups (category list) for a given content type.
 * Returns [{name, count}] sorted by name; "Uncategorized" if missing.
 */
export function groupChannels(channels, contentType) {
  // For SERIES, count UNIQUE series (de-duplicated by series_name) per
  // category — not raw episode rows. Users expect the sidebar number
  // next to "Drama" to read 42 shows, not 1,200 episodes. Live TV +
  // Movies still count rows directly.
  if (contentType === "series") {
    const seenByGroup = new Map(); // group → Set<series_name>
    for (const c of channels) {
      if (c.content_type !== "series") continue;
      const g = c.group || "Uncategorized";
      let s = seenByGroup.get(g);
      if (!s) { s = new Set(); seenByGroup.set(g, s); }
      s.add(c.series_name || c.name);
    }
    return [...seenByGroup.entries()]
      .map(([name, set]) => ({ name, count: set.size }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  const counts = new Map();
  for (const c of channels) {
    if (contentType && c.content_type !== contentType) continue;
    const g = c.group || "Uncategorized";
    counts.set(g, (counts.get(g) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Stats summary keyed by content_type. */
export function statsByType(channels) {
  const out = { live_tv: 0, movie: 0, series: 0, catchup: 0 };
  for (const c of channels) {
    if (c.content_type in out) out[c.content_type]++;
  }
  return out;
}

/** Channels that support catch-up via their M3U attributes. */
export function catchupChannels(channels) {
  return channels.filter(
    (c) =>
      c.content_type === "live_tv" &&
      (c.catchup || c.catchup_source || c.catchup_days)
  );
}


/**
 * Streaming variant — fetches and parses the playlist in one shot using
 * a `ReadableStream` reader so the entire response body never has to live
 * in memory simultaneously. Critical on Smart TVs with a ~256 MB WebView
 * heap budget against IPTV playlists that are commonly 100-200 MB on
 * the wire (590k channels in the user's case).
 *
 * Two-phase progress events:
 *   • { phase: "download", bytes }              — bytes received so far
 *   • { phase: "parse",    done, bytes }        — channels parsed so far
 *
 * Falls back to text-then-parse when `response.body` isn't a stream
 * (very old WebViews / opaque responses).
 */
export async function parseM3uStream(response, onProgress) {
  if (!response || !response.body || typeof response.body.getReader !== "function") {
    // No streaming support — fall back to whole-text parse.
    const text = await (response?.text ? response.text() : Promise.resolve(""));
    return parseM3u(text, onProgress);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  const channels = [];
  let epgUrl = null;
  let order = 0;
  let bytesIn = 0;
  let buffer = "";
  let pendingExtInf = null;
  let sawHeader = false;
  const PROGRESS_EVERY = 500;
  // Process incoming lines from `buffer` up to (but not including) the
  // last partial line. The leftover stays in `buffer` for the next chunk.
  async function flushBuffer(final) {
    let nlIdx;
    while ((nlIdx = buffer.indexOf("\n")) !== -1) {
      // "" + slice forces V8 to materialise an independent string so the
      // monstrous `buffer` can be garbage-collected once we replace it.
      let line = "" + buffer.slice(0, nlIdx);
      buffer = buffer.slice(nlIdx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (!sawHeader) {
        sawHeader = true;
        if (line.trim().startsWith("#EXTM3U")) {
          const headerAttrs = parseAttributes(line);
          epgUrl = headerAttrs["url-tvg"] || headerAttrs["x-tvg-url"] || null;
          continue;
        }
      }
      await consumeLine(line);
    }
    if (final && buffer.length > 0) {
      // Tail line (no trailing newline) — flush it.
      let line = buffer.endsWith("\r") ? buffer.slice(0, -1) : buffer;
      buffer = "";
      await consumeLine(line);
    }
  }

  async function consumeLine(rawLine) {
    const line = rawLine.trim();
    if (!line) return;
    if (line.startsWith("#EXTINF:")) {
      pendingExtInf = line;
      return;
    }
    if (line.startsWith("#")) return; // ignore other directives
    if (!pendingExtInf) return; // stray URL with no metadata — drop
    const after = pendingExtInf.slice(8);
    pendingExtInf = null;
    let inQuotes = false;
    let splitIdx = -1;
    for (let j = 0; j < after.length; j++) {
      const ch = after[j];
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === "," && !inQuotes) {
        splitIdx = j;
        break;
      }
    }
    const attrPart = splitIdx === -1 ? "" : after.slice(0, splitIdx);
    const name = (splitIdx === -1 ? after : after.slice(splitIdx + 1)).trim();
    if (!name) return;
    const attrs = parseAttributes(attrPart);
    const url = line;
    const group = attrs["group-title"] || "";
    const contentType = detectContentType(group, name, url);
    // FNV-1a 32-bit hash of the stream URL.
    let h = 0x811c9dc5;
    for (let k = 0; k < url.length; k++) {
      h ^= url.charCodeAt(k);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    const id = `c-${order}-${h.toString(36)}`;
    const channel = {
      id,
      name,
      stream_url: url,
      logo_url: attrs["tvg-logo"] || null,
      group: group || null,
      content_type: contentType,
      tvg_id: attrs["tvg-id"] || null,
      tvg_name: attrs["tvg-name"] || null,
      catchup: attrs["catchup"] || attrs["catchup-type"] || null,
      catchup_source: attrs["catchup-source"] || null,
      catchup_days:
        parseInt(attrs["catchup-days"] || attrs["timeshift"] || "", 10) || null,
      order: order++,
    };
    if (contentType === "series") Object.assign(channel, extractSeriesMeta(name));
    if (contentType === "movie") channel.year = extractYear(name);
    channels.push(channel);
    if (channels.length % PROGRESS_EVERY === 0 && typeof onProgress === "function") {
      try {
        onProgress({ phase: "parse", done: channels.length, bytes: bytesIn });
      } catch {
        /* ignore */
      }
    }
  }

  try {
    // v1.4.21 — Per-chunk read timeout. On Smart TV WebViews the
    // provider's HTTP connection can silently stall mid-download
    // (proxy timeouts, WebView network stack quirks). Without this
    // guard `reader.read()` waits FOREVER and the user is stuck on
    // the loading splash — matching the "app hangs at 92 % on TV"
    // report. If a single chunk doesn't arrive within STREAM_READ_TIMEOUT_MS
    // we abort the reader and let the outer catch fall through to
    // the non-streaming fallback path.
    const STREAM_READ_TIMEOUT_MS = 45_000;
    while (true) {
      let read;
      try {
        read = await Promise.race([
          reader.read(),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error(`m3u stream read timed out after ${STREAM_READ_TIMEOUT_MS} ms (bytesIn=${bytesIn}, channels=${channels.length})`)),
              STREAM_READ_TIMEOUT_MS,
            ),
          ),
        ]);
      } catch (timeoutErr) {
        // Cancel the underlying stream so the socket is freed on the
        // native side, then rethrow so the caller can retry.
        try { await reader.cancel(timeoutErr?.message || "timeout"); } catch { /* ignore */ }
        throw timeoutErr;
      }
      const { done, value } = read;
      if (done) break;
      bytesIn += value.byteLength;
      buffer += decoder.decode(value, { stream: true });
      // Surface raw byte progress so the splash counter ticks during the
      // initial download phase (before any channel has been parsed yet).
      if (typeof onProgress === "function") {
        try {
          onProgress({ phase: "download", bytes: bytesIn, done: channels.length });
        } catch {
          /* ignore */
        }
      }
      await flushBuffer(false);
      // Yield to the event loop after every chunk so the renderer can
      // paint the progress bar and the GC can clean up freed buffer
      // slices. Keeps the JS thread responsive on slow TV CPUs.
      await new Promise((r) => setTimeout(r, 0));
    }
    // Drain trailing decoder state + any final partial line.
    buffer += decoder.decode();
    await flushBuffer(true);
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* no-op */
    }
  }

  if (typeof onProgress === "function") {
    try {
      onProgress({ phase: "parse", done: channels.length, bytes: bytesIn });
    } catch {
      /* ignore */
    }
  }
  return { channels, epgUrl };
}
